import { ContractState, ContractViewModel, QuoteReason } from '@ail/contracts-api'
import { CustomerIndexViewModel } from '@ail/customers-api'
import { VehicleReturnStatus, VehicleReturnViewModel } from '@ail/orders-api'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { Arr } from '../../../helpers/Arr'
import { hasTimeElapsed, isDateValid, printDate } from '../../../helpers/Utils'
import { useFilteredCustomers } from '../../../hooks/useFilteredCustomers'
import { useGetContractsBySecurityKeysQuery } from '../../../redux/api/contractsApi'
import { useGetAllUnitsQuery } from '../../../redux/api/customerApi'
import { useGetVehicleReturnsQuery } from '../../../redux/api/ordersApi'
import { useAppSelector } from '../../../redux/store'
import { DriverFilter } from '../../../types/enums'
import { ColKeysEnum } from '../../GeneralContracts/GeneralContractsEnums'
import ConfirmedDataTooltip from '../../common/ConfirmedDataTooltip/ConfirmedDataTooltip'
import {
  Grid,
  GridColumn,
  InlineTextFilter,
  ModalContractsDropdownFilter,
  ModalDriverFilter,
} from '../../common/Grid/Grid'
import { LoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner'
import ObjTypeCell from '../GridCells/ObjectTypeCell/ObjectTypeCell'
import { ReturnSchedulerModal } from './ReturnSchedulerModal'
import './Returns.scss'

// #region TYPES
export interface GridRow {
  contract: ContractViewModel
  returnData?: VehicleReturnViewModel
  customer?: CustomerIndexViewModel
  replacedContract?: ContractViewModel
  futureContract?: ContractViewModel
  costCenter: string
}

type ReturnsFilters = {
  orderStatus: ModalContractsDropdownFilter<GridRow>['filterFn']
  contract: InlineTextFilter<GridRow>['filterFn']
  costCenter: ModalContractsDropdownFilter<GridRow>['filterFn']
  replacedContractsRefNumber: InlineTextFilter<GridRow>['filterFn']
  makes: ModalContractsDropdownFilter<GridRow>['filterFn']
  model: InlineTextFilter<GridRow>['filterFn']
  driver: ModalDriverFilter<GridRow>['filterFn']
  gallikerReference: InlineTextFilter<GridRow>['filterFn']
  product: ModalContractsDropdownFilter<GridRow>['filterFn']
  nature: ModalContractsDropdownFilter<GridRow>['filterFn']
}
// #endregion

export default function Returns(props: {
  companies?: string[]
  updateDeliveryCompanySelection: (companies?: string[]) => void
}) {
  const user = useAppSelector((state) => state.appData.currentUser)
  const [selectedCustomerKeys, setSelectedCustomersKeys] = useState<string[]>()
  const [manualSelection, setManualSelection] = useState(false)
  const [selectedDriver, setSelectedDriver] = useState<DriverFilter>()
  const [calendarInfo, setCalendarInfo] = useState<GridRow>()
  const [calendarVisible, setCalendarVisible] = useState(false)
  const [previewReturnData, setPreviewReturnData] = useState<{
    open: boolean
    contractId: string | undefined
  }>({
    open: false,
    contractId: undefined,
  })

  // #region CUDSTOM HOOKS
  const { t } = useTranslation('common')
  const allUnits = useGetAllUnitsQuery()
  const contracts = useGetContractsBySecurityKeysQuery(
    { securityKeys: selectedCustomerKeys! },
    { skip: !selectedCustomerKeys?.length }
  )

  const { customers: filteredCustomers } = useFilteredCustomers({
    status: 'ACTIVE',
  })
  const vehicleReturns = useGetVehicleReturnsQuery()
  // #endregion
  // #region DATA MAPS
  const customerMap = useMemo(() => Arr.toHashMap(filteredCustomers ?? [], (c) => c.value!), [filteredCustomers])
  const unitsMap = useMemo(() => Arr.toHashMap(allUnits.data ?? [], (c) => c.value!), [allUnits.data])

  const returnDataMap = useMemo(
    () =>
      Arr.toHashMap(vehicleReturns.data ?? [], (c) =>
        c.logisticId?.endsWith('R') ? c.logisticId.slice(0, -1) : c.logisticId!
      ),
    [vehicleReturns.data]
  )
  // #endregion

  //#region Functions & Callbacks
  useEffect(() => {
    if (manualSelection) return
    setSelectedCustomersKeys(props.companies)
  }, [props.companies, manualSelection])

  function displayBasicValue(value: string, customTitle?: string) {
    return <span title={customTitle || value}>{value}</span>
  }
  const getCellClass = useCallback((row: GridRow) => {
    // const status = getOrderStatus(row)
    const status = row.returnData?.status

    // return `contractStatus contractStatus-${status?.toLowerCase()} ${
    //   row.vehicleOrder &&
    //   row.vehicleOrder.status === VehicleOrderStatus.PendingDeliveryConfirmation
    //     ? row.vehicleOrder.status
    //     : ''
    // }`
    return `contractStatus contractStatus-${status?.toLowerCase()}`
  }, [])
  // const getOrderStatus = useCallback((row: GridRow) => {
  //   return row.vehicleOrder
  //     ? getVehicleOrderStatus(row.vehicleOrder)
  //     : defineContractStateNew(row.contract)
  // }, [])

  const showRestrictedReturnDate = useCallback(
    (row: GridRow): string | React.ReactElement => {
      if (!row.returnData?.returnDate) return '-'

      const statusMatch = [VehicleReturnStatus.Returned, VehicleReturnStatus.Completed].includes(row.returnData.status)

      return statusMatch ? (
        printDate(row.returnData?.returnDate)
      ) : user?.isInternalUser ? (
        <span style={{ color: 'red' }}>{printDate(row.returnData?.returnDate)}</span>
      ) : (
        '-'
      )
    },
    [user?.isInternalUser]
  )

  const handleCustomerSelection = useCallback(
    (customers?: string[]) => {
      const validKeys = customers?.filter((c) => customerMap[c])
      const keys = validKeys?.length ? validKeys : undefined
      setSelectedCustomersKeys(keys)
      props.updateDeliveryCompanySelection(keys)
      setManualSelection(true)
    },
    [props, customerMap]
  )

  useEffect(() => {
    return () => setManualSelection(false)
  }, [])
  const getDriverName = useCallback(
    (row: GridRow) => {
      const c = row.contract
      const driverName = c.vehicle?.driver?.displayName
      const value =
        !!driverName && !c.poolCar
          ? driverName
          : !c.poolCar && !driverName
          ? t('NOT_ASSIGNED')
          : c.poolCar && !!c.location
          ? `${t('POOL_CAR')} | ${c.location}`
          : c.poolCar && (!c.location || !!driverName)
          ? `${t('POOL_CAR')}`
          : ''
      return value
    },
    [t]
  )
  const getReplacedContracts = useCallback(
    (contract: ContractViewModel) => {
      const futureContract = contracts?.currentData?.find((el) => {
        if (el?.replacedContractId === contract.id) {
          return el
        }
      })
      const replacedContract = contracts.currentData?.find((c) => c.id === contract.replacedContractId)
      return { futureContract, replacedContract }
    },
    [contracts.currentData]
  )

  const getCostCenterName = useCallback(
    (contract: ContractViewModel) => {
      if (unitsMap && contract.owner) {
        const relatedUnit = unitsMap[contract.owner]
        return relatedUnit && relatedUnit.unit ? relatedUnit.unit.name : t('NO_COST_CENTER')
      }
      return ''
    },
    [t, unitsMap]
  )
  //#endregion

  // #region DataRows
  const dataRows = useMemo(() => {
    const data: GridRow[] = []
    let customer: CustomerIndexViewModel | undefined
    const initializedContracts = contracts.currentData?.filter(
      (c) =>
        c.state === ContractState.Terminated ||
        c.state === ContractState.Running ||
        c.state === ContractState.Closed ||
        c.state === ContractState.Extended
    )
    //! NEXT API RELEASE TO PROD DOES NOT USE EXTRA ANYMORE
    //! CORRECT ?.filter((c) => !!returnDataMap[c.vehicle?.['GallikerReference']
    initializedContracts
      ?.filter((c) => !!returnDataMap[c.vehicle?.['GallikerReference']])
      .forEach((contract) => {
        const returnData = returnDataMap[contract.vehicle?.['GallikerReference']]

        const { replacedContract, futureContract } = getReplacedContracts(contract)
        if (contract.owner) {
          const [customerKey] = contract.owner.split('//')
          if (customerKey) {
            customer = customerMap[customerKey]
          }
        }

        const costCenter = getCostCenterName(contract)
        data.push({
          contract,
          returnData,
          replacedContract,
          futureContract,
          customer,
          costCenter,
        })
      })
    return data
  }, [contracts, returnDataMap, getReplacedContracts, getCostCenterName, customerMap])

  const selectorOptionsList = useMemo(() => {
    const uniqueStatuses = new Set<string>()
    const uniqueNatures = new Set<string>()
    const uniqueMakes = new Set<string>()
    dataRows.forEach((row) => {
      const returnStatus = row.returnData?.status

      if (returnStatus) {
        uniqueStatuses.add(returnStatus)
      }

      if (row.contract.vehicle?.nature) {
        uniqueNatures.add(row.contract.vehicle.nature)
      }
      if (row.contract.vehicle?.makeName) {
        uniqueMakes.add(row.contract.vehicle?.makeName)
      }
    })
    return {
      status: [...uniqueStatuses].sort(),
      nature: [...uniqueNatures].sort(),
      makes: [...uniqueMakes].sort(),
    }
  }, [dataRows])

  // #region FilterFNS
  const filterFns: ReturnsFilters = useMemo(() => {
    return {
      costCenter: () => true,
      product: () => true,
      contract: (item, value) => !!item.contract.reference?.includes(value),
      orderStatus: (item, value) => {
        const status = item.returnData?.status
        if (status && value.includes(status)) {
          return true
        }
        return false
      },
      replacedContractsRefNumber: (item, value) => {
        if (
          item.futureContract ||
          (item?.contract?.quoteReason === QuoteReason.Replacement && !!item?.contract.replacedContractId)
        ) {
          if (item.futureContract?.reference?.includes(value) || item.replacedContract?.reference?.includes(value)) {
            return true
          }
        }
        return false
      },
      makes: (item, value) => !!item.contract.vehicle?.makeName && value.includes(item.contract.vehicle.makeName),
      model: (item, value) =>
        !!item.contract.vehicle?.modelName &&
        item.contract.vehicle.modelName.toLowerCase().includes(value.toLowerCase()),
      driver: (item, value) => {
        const c = item.contract
        const driverName = c.vehicle?.driver?.displayName
        const poolcar = 'Poolcar'
        const notAssigned = 'Not assigned'

        const result =
          !!driverName && !c.poolCar
            ? driverName
            : !c.poolCar && !driverName
            ? notAssigned
            : c.poolCar && !!c.location
            ? `${poolcar} | ${c.location}`
            : c.poolCar && (!c.location || !!driverName)
            ? poolcar
            : ''
        const matchesDriver = value.drivers && !c.poolCar && !!driverName
        const matchesPoolcar = value.pool_cars && result.includes(poolcar)
        const matchesNotAssigned = value.not_assigned && result.includes(notAssigned)

        const matchesSearchString =
          !!value.searchString && result.toLowerCase().includes(value.searchString.toLowerCase())

        if (value.drivers && matchesDriver) {
          return matchesSearchString || !value.searchString
        }

        if (value.pool_cars && matchesPoolcar) {
          return matchesSearchString || !value.searchString
        }

        if (value.not_assigned && matchesNotAssigned) {
          return matchesSearchString || !value.searchString
        }

        if (!value.drivers && !value.pool_cars && !value.not_assigned) {
          return matchesSearchString
        }

        return false
      },
      gallikerReference: (item, value) => {
        const galReference: string = item.contract.vehicle?.['GallikerReference']
        if (galReference && galReference.toLowerCase().includes(value.toLowerCase())) {
          return true
        }
        return false
      },
      nature: (item, value) => !!item.contract.vehicle?.nature && value.includes(item.contract.vehicle?.nature),
    }
  }, [])
  // #endregion
  // #region Columns
  const columns = useMemo((): GridColumn<GridRow>[] => {
    const disableClearBtn =
      (filteredCustomers?.length === 0 && selectedCustomerKeys?.length === 1) ||
      (filteredCustomers?.length === 1 && filteredCustomers[0].value === selectedCustomerKeys?.[0])

    const openCalendar = (row: GridRow) => {
      const { contract, returnData } = row
      if (!contract.requestId && !returnData) {
        return
      }
      setCalendarInfo(row)
      setCalendarVisible(true)
    }

    const handlePreviewData = (row: GridRow) => {
      if (previewReturnData.open) setPreviewReturnData({ open: false, contractId: undefined })
      else setPreviewReturnData({ open: true, contractId: row.contract.id })
    }
    const canPreviewData = (row: GridRow) => {
      return previewReturnData.open && previewReturnData.contractId === row.contract.id ? true : false
    }
    const cols: GridColumn<GridRow>[] = [
      {
        key: ColKeysEnum.company,
        name: t('COMPANY'),
        width: 320,
        resizable: false,
        filter: {
          type: 'modal_delivery_customer_filter',
          data: filteredCustomers,
          format: (value) => value?.map((c) => customerMap[c]?.customer?.name ?? ''),
          onChange: (value) => handleCustomerSelection(value),
          value: selectedCustomerKeys,
          readOnly: disableClearBtn,
          cascadeClear: ColKeysEnum.costCenter,
          autoTrigger: !props.companies?.length,
          ignoreSaved: !!props.companies?.length,
        },
        renderCell: ({ row }) => displayBasicValue(row.customer?.customer?.name ?? ''),
        cellClass: getCellClass,
        exportFormat: (row) => row.customer?.customer?.name,
        sortBy: (row) => row.customer?.customer?.name,
      },
      {
        key: ColKeysEnum.nature,
        name: t('OBJ_TYPE'),
        width: 90,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('OBJ_TYPE'),
          filterFn: filterFns.nature,
          data: selectorOptionsList.nature,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          if (row.contract.vehicle?.nature) {
            return <ObjTypeCell nature={row.contract.vehicle?.nature} />
          }
          return null
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.nature,
        sortBy: (row) => row.contract.vehicle?.nature,
      },
      {
        key: ColKeysEnum.driverName,
        name: t('DRIVER_NAME'),
        width: 150,
        minWidth: 150,
        resizable: false,
        filter: {
          type: 'modal_driver_filter',
          filterFn: filterFns.driver,
          format: (value) => {
            if (!value) {
              return
            }
            const result = []
            if (value.pool_cars) result.push(t('POOL_CARS'))
            if (value.not_assigned) result.push(t('NOT_ASSIGNED'))
            if (value.drivers) result.push(t('DRIVERS'))
            if (value.searchString) result.push(value.searchString)
            return result
          },
          value: selectedDriver,
          onChange: (value) => setSelectedDriver(value),
        },
        renderCell: ({ row }) => displayBasicValue(getDriverName(row)),
        cellClass: getCellClass,
        exportFormat: getDriverName,
        sortBy: getDriverName,
      },
      {
        key: ColKeysEnum.gallikerReference,
        name: t('LOGISTICID'),
        width: 100,
        minWidth: 150,
        resizable: false,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.gallikerReference,
        },
        renderCell: ({ row }) => displayBasicValue(row.contract.vehicle?.['GallikerReference']),
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.['GallikerReference'],
        sortBy: (row) => row.contract.vehicle?.['GallikerReference'],
      },
      {
        key: ColKeysEnum.contract,
        name: t('CONTRACT'),
        width: 140,
        resizable: false,
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.contract,
        },
        renderCell({ row }) {
          return <NavLink to={`/orderAndDelivery/details/${row.contract.id}`}>{row.contract.reference}</NavLink>
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.reference,
        sortBy: (row) => row.contract.reference,
      },
      {
        key: ColKeysEnum.vehicle,
        width: 80,
        name: t('MAKES'),
        label: t('MAKES_AND_MODEL'),
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('MAKES'),
          filterFn: filterFns.makes,
          data: selectorOptionsList.makes,
        },
        renderCell({ row }) {
          const vehicle = row.contract.vehicle
          if (vehicle?.makeName && vehicle?.makeName.length >= 2) {
            const logos =
              vehicle?.makeName.toLowerCase().indexOf('ladestation') > -1
                ? 'dummy'
                : vehicle?.makeName.replace(/\s+/g, '-').toLowerCase()
            return (
              <div className="vehicleDescription">
                {logos && (
                  <img
                    src={`../../../../BW/${logos.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}.svg`}
                    width="23"
                    height="23"
                  />
                )}
                {vehicle.description && displayBasicValue(vehicle.description)}
              </div>
            )
          }
          return null
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.contract.vehicle?.description,
        sortBy: (row) => row.contract.vehicle?.makeName,
      },
      {
        key: ColKeysEnum.model,
        name: t('MODEL'),
        minWidth: 180,
        resizable: false,
        parentKey: ColKeysEnum.vehicle,
        filter: { type: 'inline_text_filter', filterFn: filterFns.model },
        sortBy: (row) => row.contract.vehicle?.description,
      },
      {
        key: ColKeysEnum.replacedContractsRefNumber,
        name: (
          <i
            title={t('REPLACED_CONTRACT')}
            className={'icon-change-car'}
            style={{ fontSize: '18px', verticalAlign: 'sub', margin: '0px 5px' }}
          />
        ),
        label: t('REPLACED_CONTRACT'),
        filter: {
          type: 'inline_text_filter',
          filterFn: filterFns.replacedContractsRefNumber,
        },
        width: 60,
        resizable: false,
        renderCell({ row }) {
          // TODO: extract to component
          const replacement = row.contract.quoteReason === QuoteReason.Replacement && row.contract.replacedContractId
          const contractId = replacement ? row.contract.replacedContractId : row.futureContract?.id
          const reference = replacement ? row.replacedContract?.reference : row.futureContract?.reference
          return (
            contractId && (
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <NavLink
                  style={{ color: '#39B54A', fontSize: '20px' }}
                  title={reference ?? ''}
                  className="icon-change-car"
                  to={`/generalContracts/details/${contractId}`}
                />
              </div>
            )
          )
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.futureContract?.reference || row.replacedContract?.reference,
        sortBy: (row) => {
          if (
            row.replacedContract ||
            (row?.contract?.quoteReason === QuoteReason.Replacement && !!row?.contract.replacedContractId)
          ) {
            return row.futureContract?.reference || row.replacedContract?.reference
          }
        },
      },
      {
        key: ColKeysEnum.imposedReturnDate,
        name: t('RETURN_EXPECTED'),
        width: 150,
        resizable: false,
        renderCell: ({ row }) => printDate(row.contract.imposedReturnDate),
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.contract.imposedReturnDate) ? printDate(row.contract.imposedReturnDate) : undefined,
        sortBy: (row) =>
          row.contract.imposedReturnDate && isDateValid(row.contract.imposedReturnDate)
            ? new Date(row.contract.imposedReturnDate)
            : undefined,
      },
      {
        key: ColKeysEnum.returnProposed,
        name: t('RETURN_PROPOSED'),
        width: 150,
        resizable: false,
        renderCell({ row }) {
          return <span>{printDate(row.returnData?.proposedReturnDate)}</span>
        },
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.returnData?.proposedReturnDate) ? printDate(row.returnData?.proposedReturnDate) : undefined,
        sortBy: (row) =>
          row.returnData?.proposedReturnDate && isDateValid(row.returnData?.proposedReturnDate)
            ? new Date(row.returnData?.proposedReturnDate)
            : undefined,
      },
      {
        key: ColKeysEnum.returnScheduled,
        name: t('RETURN_SCHEDULED'),
        width: 150,
        resizable: false,
        renderCell({ row }) {
          const scheduledDate = row.returnData?.scheduledReturnDate
          if (scheduledDate) {
            const address = row.returnData?.scheduledReturnAddress
            const contact = row.returnData?.scheduledReturnContact
            return (
              <span className="confirmedDelivery">
                <i className="icon-roles" /> {printDate(scheduledDate)}
                <i className="icon-marker" onClick={() => handlePreviewData(row)} />
                {canPreviewData(row) && <ConfirmedDataTooltip address={address} contact={contact} returns />}
              </span>
            )
          }
          const proposedReturnDate = row.returnData?.proposedReturnDate

          const orderCompleted = row.returnData?.status
            ? [VehicleReturnStatus.Returned, VehicleReturnStatus.Completed].includes(row.returnData?.status)
            : undefined
          if (!!proposedReturnDate && !orderCompleted) {
            const validTimeWindow = hasTimeElapsed(proposedReturnDate, 5, 'Days', true)
            if (validTimeWindow) {
              return (
                <span>
                  <button className="btnDate" onClick={() => openCalendar(row)}>
                    <i className="icon-calendar" /> {t('SELECT_DATE')}
                  </button>
                </span>
              )
            }
          }
        },
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.returnData?.scheduledReturnDate) ? printDate(row.returnData?.scheduledReturnDate) : undefined,
        sortBy: (row) =>
          row.returnData?.scheduledReturnDate && isDateValid(row.returnData?.scheduledReturnDate)
            ? new Date(row.returnData?.scheduledReturnDate)
            : undefined,
      },
      {
        key: ColKeysEnum.returnDate,
        name: t('RETURN_DATE'),
        width: 100,
        resizable: false,
        renderCell: ({ row }) => showRestrictedReturnDate(row),
        cellClass: getCellClass,
        exportFormat: (row) =>
          isDateValid(row.returnData?.returnDate) ? printDate(row.returnData?.returnDate) : undefined,
        sortBy: (row) =>
          row.returnData?.returnDate && isDateValid(row.returnData?.returnDate)
            ? new Date(row.returnData?.returnDate)
            : undefined,
      },
      {
        key: ColKeysEnum.returnProtocol,
        name: t('RETURN_PROTOCOL'),
        width: 90,
        resizable: false,
        renderCell({ row }) {
          return (
            <div className="returnsDocumentCell">
              <a href={`/orderAndDelivery/details/${row.contract.id}`}>
                <i
                  className={'icon-protocol'}
                  style={{ color: row.returnData?.returnProtocol ? '#60C36E' : '#A7A7A7' }}
                />
              </a>
            </div>
          )
        },
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.returnDocument,
        name: t('RETURN_DOCUMENTS'),
        width: 90,
        resizable: false,
        renderCell({ row }) {
          return (
            <div className="returnsDocumentCell">
              <a href={`/orderAndDelivery/details/${row.contract.id}`}>
                <i className={'icon-doc'} style={{ color: row.returnData?.returnDocuments ? '#60C36E' : '#A7A7A7' }} />
              </a>
            </div>
          )
        },
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.finalCalculation,
        name: t('RETURN_FINAL_CALCULATION'),
        width: 90,
        resizable: false,
        renderCell({ row }) {
          return (
            <div className="returnsDocumentCell">
              <a href={`/orderAndDelivery/details/${row.contract.id}`}>
                <i
                  className={'icon-doc-invoice'}
                  style={{ color: row.returnData?.finalCalculation ? '#60C36E' : '#A7A7A7' }}
                />
              </a>
            </div>
          )
        },
        cellClass: getCellClass,
      },
      {
        key: ColKeysEnum.state,
        name: t('STATUS'),
        width: 150,
        resizable: false,
        filter: {
          type: 'modal_contracts_dropdown_filter',
          title: t('RETURN_STATUS_SEARCH'),
          filterFn: filterFns.orderStatus,
          data: selectorOptionsList.status,
          format: (items) => items?.map((v) => t(`${v.toUpperCase()}`)),
        },
        renderCell({ row }) {
          const vehicleOrderStatus = row.returnData?.status
          return vehicleReturns.data ? (
            <label
              className={`contractListStatus statusArea ${vehicleOrderStatus?.toLowerCase()}`}
              title={t(`${vehicleOrderStatus}`.toUpperCase())}
            >
              {t(`${vehicleOrderStatus}`.toUpperCase())}
            </label>
          ) : (
            <div style={{ display: 'flex', margin: 'auto' }}>
              <LoadingSpinner />
            </div>
          )
        },
        cellClass: getCellClass,
        exportFormat: (row) => row.returnData?.status,
        sortBy: (row) => row.returnData?.status,
      },
    ]
    return cols
  }, [
    customerMap,
    filterFns,
    filteredCustomers,
    getCellClass,
    getDriverName,
    handleCustomerSelection,
    props.companies?.length,
    selectedCustomerKeys,
    selectedDriver,
    selectorOptionsList,
    t,
    vehicleReturns.data,
    showRestrictedReturnDate,
    previewReturnData,
  ])
  //#endregion

  return (
    <>
      <Grid
        id="Returns"
        columns={columns}
        rows={dataRows}
        rowKeyGetter={(item) => {
          return item.contract.id
        }}
        pagination={true}
        showTotalItems={true}
        onReload={
          selectedCustomerKeys?.length
            ? () => {
                vehicleReturns.refetch()
                contracts.refetch()
              }
            : undefined
        }
        exportTitle="Returns"
      />
      {calendarVisible && calendarInfo && (
        <ReturnSchedulerModal
          visible={calendarVisible}
          onClose={() => {
            setCalendarVisible(false)
            setCalendarInfo(undefined)
          }}
          dataRow={calendarInfo}
        />
      )}
    </>
  )
}
